// @ts-nocheck
/**
 * New Call Script — redesigned to obd3-gui pattern (ScrollPage + PageHeader
 * + Card). 3-field form: title, short_description, description (CKEditor).
 * Client-side title uniqueness snapshot preserved.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Save } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { API_CALL_SCRIPT_CREATE, API_CALL_SCRIPT_LIST } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import {
  getScriptTitleValidationError,
  getLengthError,
  SCRIPT_SHORT_DESCRIPTION_MAX,
  SCRIPT_DESCRIPTION_MAX,
  SCRIPT_EDITOR_CONFIG,
} from '@/utilities/helpers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';

const CallScriptCreate = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [title, setTitle] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [existingTitles, setExistingTitles] = useState<string[]>([]);

  useEffect(() => {
    checkAuth(
      () => {},
      () => {},
    );
    getApiCall(API_CALL_SCRIPT_LIST)
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : resp?.data ?? [];
        setExistingTitles(list.map((s: any) => s.title?.trim().toLowerCase()));
      })
      .catch(() => {
        toast.error("Can't load existing scripts");
      });
  }, []);

  const validate = () => {
    const errors: any = {};
    const titleError = getScriptTitleValidationError(title);
    if (titleError) {
      errors.title = titleError;
      toast.error(titleError);
    } else if (existingTitles.includes(title.trim().toLowerCase())) {
      errors.title = 'Title must be unique';
      toast.error('Title must be unique');
    }
    if (!shortDescription.trim())
      errors.shortDescription = 'Short description is required';
    else {
      const shortError = getLengthError(
        shortDescription,
        SCRIPT_SHORT_DESCRIPTION_MAX,
        'Short description',
      );
      if (shortError) errors.shortDescription = shortError;
    }
    if (!description.trim()) errors.description = 'Description is required';
    else {
      const descError = getLengthError(description, SCRIPT_DESCRIPTION_MAX, 'Description');
      if (descError) errors.description = descError;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitForm = (e?: any) => {
    e?.preventDefault();
    if (!validate()) return;
    setSaving(true);
    postApiCall(API_CALL_SCRIPT_CREATE, {
      title,
      short_description: shortDescription,
      description,
    })
      .then(() => {
        toast.success('Script created successfully');
        setTimeout(() => navigate(`/${ROUTES.CALL_SCRIPT.LIST}`), 1000);
      })
      .catch((err: any) => {
        const message =
          err?.message || err?.response?.data?.message || err || 'Something went wrong';
        if (typeof message === 'string' && message.toLowerCase().includes('already exists')) {
          setFieldErrors((prev: any) => ({ ...prev, title: message }));
        } else {
          toast.error(message);
        }
        setSaving(false);
      });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="New Call Script"
        description="Create a rich-text script agents can read during live calls."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.CALL_SCRIPT.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      <form className="space-y-4" onSubmit={submitForm}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              Script content
            </CardTitle>
            <CardDescription>Title must be unique. Description supports rich text.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (e.target.value.trim())
                      setFieldErrors((prev: any) => ({ ...prev, title: '' }));
                  }}
                  placeholder="Enter script title"
                  className={cn(
                    fieldErrors.title && 'border-destructive focus-visible:ring-destructive',
                  )}
                />
                {fieldErrors.title && (
                  <p className="text-xs text-destructive">{fieldErrors.title}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="short_description">
                  Short Description <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="short_description"
                  type="text"
                  maxLength={SCRIPT_SHORT_DESCRIPTION_MAX}
                  value={shortDescription}
                  onChange={(e) => {
                    setShortDescription(e.target.value);
                    if (e.target.value.trim())
                      setFieldErrors((prev: any) => ({ ...prev, shortDescription: '' }));
                  }}
                  placeholder="One-line summary"
                  className={cn(
                    fieldErrors.shortDescription &&
                      'border-destructive focus-visible:ring-destructive',
                  )}
                />
                {fieldErrors.shortDescription && (
                  <p className="text-xs text-destructive">{fieldErrors.shortDescription}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Description <span className="text-destructive">*</span>
              </Label>
              <div
                className={cn(
                  'overflow-hidden rounded-md border',
                  fieldErrors.description ? 'border-destructive' : 'border-border',
                )}
              >
                <CKEditor
                  editor={ClassicEditor}
                  config={SCRIPT_EDITOR_CONFIG}
                  data={description}
                  onChange={(event: any, editor: any) => {
                    const data = editor.getData();
                    setDescription(data);
                    if (data.trim())
                      setFieldErrors((prev: any) => ({ ...prev, description: '' }));
                  }}
                  onReady={(editor: any) => {
                    editor.editing.view.change((writer: any) => {
                      writer.setStyle(
                        'height',
                        '300px',
                        editor.editing.view.document.getRoot(),
                      );
                    });
                  }}
                />
              </div>
              {fieldErrors.description && (
                <p className="text-xs text-destructive">{fieldErrors.description}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to={`/${ROUTES.CALL_SCRIPT.LIST}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Adding…' : 'Add script'}
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
};

export default CallScriptCreate;
